import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Upload, Download, FileText, FileSpreadsheet, CheckCircle2,
  AlertTriangle, Link2, Sparkles, Layers,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const PO_LABEL = {
  open: 'Open', partially_received: 'Partially Received', fully_received: 'Fully Received',
  draft: 'Draft', sent: 'Sent', cancelled: 'Cancelled',
};

// Small editable cell — surfaces "leave blank if not confident" cases
const EditCell = ({ value, onChange, type = 'text', className, testid, placeholder }) => (
  <Input
    type={type}
    value={value ?? ''}
    onChange={(e) => onChange(type === 'number' ? (e.target.value === '' ? null : parseFloat(e.target.value)) : e.target.value)}
    className={cn('h-8 px-2 py-1 text-sm', className)}
    placeholder={placeholder}
    data-testid={testid}
  />
);

/**
 * Supplier Invoice Import — PDF is the primary input; Excel/CSV is a fallback.
 * Extracted rows are editable before the user posts to a PO.
 */
const SupplierInvoiceImport = ({ open, onOpenChange, vendors, products, onPosted }) => {
  const [vendorId, setVendorId] = useState('');
  const [poId, setPoId] = useState('');
  const [poList, setPoList] = useState([]);
  const [poDetail, setPoDetail] = useState(null);
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [invoiceDate, setInvoiceDate] = useState('');
  const [file, setFile] = useState(null);

  // NEW: System selection for `System + Supplier SKU → Buyer Code` resolution.
  // Reads distinct list from `/system-supplier-mappings/systems` (auto-populated
  // as new Systems are added — no code changes required).
  const [systemsList, setSystemsList] = useState([]);
  const [selectedSystem, setSelectedSystem] = useState('');
  const [resolvingSSM, setResolvingSSM] = useState(false);

  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState(null);
  const [posting, setPosting] = useState(false);

  const [mappingRowNum, setMappingRowNum] = useState(null);
  const [mapPickerProductId, setMapPickerProductId] = useState('');
  const [mapPickerSearch, setMapPickerSearch] = useState('');
  const [savingMap, setSavingMap] = useState(false);

  // NEW: state for the inline "Map Now" panel that creates a System-Supplier
  // mapping without leaving the invoice workflow.
  const [ssmRowNum, setSsmRowNum] = useState(null);
  const [ssmBuyerCode, setSsmBuyerCode] = useState('');
  const [ssmBuyerDesc, setSsmBuyerDesc] = useState('');
  const [savingSsm, setSavingSsm] = useState(false);

  // Product linkage inside Map Now — either pick an existing Product from
  // the Product Master (preferred; guarantees no duplicate) or create a new
  // Product when it genuinely doesn't exist yet.
  const [ssmProductId, setSsmProductId] = useState('');
  const [ssmProductQuery, setSsmProductQuery] = useState('');
  const [ssmCreatingNew, setSsmCreatingNew] = useState(false);
  const [ssmNewProduct, setSsmNewProduct] = useState({ name: '', brand: '', size: '', hsn_code: '', gst_percentage: 5, purchase_price: 0 });
  const [availableProducts, setAvailableProducts] = useState([]);

  const isPdf = useMemo(() => (file?.name || '').toLowerCase().endsWith('.pdf'), [file]);

  const productById = useMemo(() => {
    const m = {};
    (products || []).forEach(p => { m[p.id] = p; });
    return m;
  }, [products]);

  const filteredProducts = useMemo(() => {
    const q = mapPickerSearch.trim().toLowerCase();
    if (!q) return products || [];
    return (products || []).filter(p =>
      (p.name || '').toLowerCase().includes(q) ||
      (p.brand || '').toLowerCase().includes(q) ||
      (p.size || '').toLowerCase().includes(q) ||
      (p.buyer_item_code || '').toLowerCase().includes(q)
    );
  }, [products, mapPickerSearch]);

  const reset = () => {
    setVendorId(''); setPoId(''); setPoList([]); setPoDetail(null);
    setInvoiceNumber(''); setInvoiceDate(''); setFile(null); setPreview(null);
    setMappingRowNum(null); setMapPickerProductId(''); setMapPickerSearch('');
    setSelectedSystem('');
    setSsmRowNum(null); setSsmBuyerCode(''); setSsmBuyerDesc('');
    setSsmProductId(''); setSsmProductQuery(''); setSsmCreatingNew(false);
    setSsmNewProduct({ name: '', brand: '', size: '', hsn_code: '', gst_percentage: 5, purchase_price: 0 });
  };
  useEffect(() => { if (!open) reset(); }, [open]);

  // Load the distinct list of Systems available in the mapping master.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const [systemsRes, productsRes] = await Promise.all([
          axios.get(`${API_URL}/system-supplier-mappings/systems`),
          axios.get(`${API_URL}/products`),
        ]);
        if (!cancelled) {
          setSystemsList(systemsRes.data?.systems || []);
          setAvailableProducts(productsRes.data || []);
        }
      } catch (_e) {
        if (!cancelled) { setSystemsList([]); setAvailableProducts([]); }
      }
    })();
    return () => { cancelled = true; };
  }, [open]);

  useEffect(() => {
    if (!vendorId) { setPoList([]); setPoId(''); setPoDetail(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/purchase-orders`, { params: { vendor_id: vendorId } });
        if (cancelled) return;
        setPoList((res.data || []).filter(p => !['cancelled', 'fully_received'].includes(p.status)));
      } catch (_e) {
        if (!cancelled) toast.error('Failed to load Purchase Orders');
      }
    })();
    return () => { cancelled = true; };
  }, [vendorId]);

  useEffect(() => {
    if (!poId) { setPoDetail(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/purchase-orders/${poId}`);
        if (!cancelled) setPoDetail(res.data);
      } catch (_e) {
        if (!cancelled) toast.error('Failed to load PO details');
      }
    })();
    return () => { cancelled = true; };
  }, [poId]);

  const handleFileChange = (e) => {
    setPreview(null);
    const f = e.target.files?.[0];
    if (f) setFile(f);
  };

  const downloadTemplate = async () => {
    try {
      const res = await axios.get(`${API_URL}/purchases/supplier-invoice/template`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = 'supplier_invoice_template.csv';
      document.body.appendChild(a); a.click();
      setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 100);
      toast.success('Template downloaded');
    } catch (_e) { toast.error('Failed to download template'); }
  };

  const runPreview = async () => {
    if (!vendorId) { toast.error('Select a vendor'); return; }
    if (!selectedSystem) { toast.error('Select a System before uploading the supplier invoice'); return; }
    if (!file) { toast.error('Upload a PDF or Excel/CSV file'); return; }
    setPreviewing(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const params = new URLSearchParams({ vendor_id: vendorId });
      if (poId) params.set('po_id', poId);
      let endpoint;
      if (isPdf) {
        endpoint = `${API_URL}/purchases/supplier-invoice/parse-pdf?${params.toString()}`;
      } else {
        if (!invoiceNumber.trim()) { toast.error('Enter the supplier invoice number'); setPreviewing(false); return; }
        if (!invoiceDate) { toast.error('Enter the supplier invoice date'); setPreviewing(false); return; }
        params.set('invoice_number', invoiceNumber);
        params.set('invoice_date', invoiceDate);
        endpoint = `${API_URL}/purchases/supplier-invoice/parse?${params.toString()}`;
      }
      const res = await axios.post(endpoint, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const enriched = await resolveSystemBuyerCodes(res.data, selectedSystem);
      setPreview(enriched);
      // Auto-populate invoice header from extraction (PDF path only)
      if (isPdf) {
        if (res.data.invoice_number) setInvoiceNumber(res.data.invoice_number);
        if (res.data.invoice_date) setInvoiceDate(res.data.invoice_date);
      }
      const s = enriched.summary;
      if (res.data.extraction_method === 'ocr') toast.info('Scanned PDF detected — used OCR');
      if (s.system_unmapped > 0) toast.warning(`${s.system_unmapped} row(s) have no Buyer Code mapping for ${selectedSystem} — map them before posting`);
      else if (s.unmapped > 0) toast.warning(`${s.unmapped} item(s) unmapped to a Product`);
      else if (s.invalid > 0) toast.error(`${s.invalid} row(s) need review`);
      else toast.success('All rows resolved via System mapping');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Preview failed');
    } finally { setPreviewing(false); }
  };

  // Resolve each extracted row's Buyer Code + Buyer Description via
  // `System + Supplier SKU`. Never guesses; unmapped rows show
  // "MAPPING NOT FOUND" with a "Map Now" action.
  const resolveSystemBuyerCodes = async (rawPreview, system) => {
    if (!rawPreview || !Array.isArray(rawPreview.rows) || !system) return rawPreview;
    setResolvingSSM(true);
    try {
      const payload = {
        system,
        rows: rawPreview.rows.map(r => ({
          row_key: String(r.row_number),
          supplier_sku: r.supplier_item_code || '',
        })),
      };
      const res = await axios.post(`${API_URL}/system-supplier-mappings/resolve-batch`, payload);
      const byKey = new Map((res.data?.resolutions || []).map(r => [r.row_key, r]));
      const rows = rawPreview.rows.map(r => {
        const m = byKey.get(String(r.row_number));
        const mapped = !!(m && m.resolved);
        // Prefill product_id from System Mapping when the row's Product isn't
        // already resolved via the legacy vendor+SKU mapping. Never override
        // an existing Product mapping — that flow keeps working as before.
        const nextProductId = (mapped && m.product_id && !r.product_id) ? m.product_id : r.product_id;
        const nextProductName = (mapped && m.product_name && !r.product_name) ? m.product_name : r.product_name;
        return {
          ...r,
          product_id: nextProductId,
          product_name: nextProductName,
          system_buyer_code: mapped ? m.buyer_item_code : null,
          system_buyer_description: mapped ? m.buyer_item_description : null,
          system_mapping_status: mapped ? 'mapped' : 'unmapped',
        };
      });
      const system_unmapped = rows.filter(r => r.system_mapping_status === 'unmapped').length;
      return { ...rawPreview, rows, summary: { ...rawPreview.summary, system_unmapped } };
    } catch (err) {
      toast.error(err.response?.data?.detail || 'System mapping lookup failed');
      const rows = rawPreview.rows.map(r => ({ ...r, system_mapping_status: 'unmapped', system_buyer_code: null, system_buyer_description: null }));
      return { ...rawPreview, rows, summary: { ...rawPreview.summary, system_unmapped: rows.length } };
    } finally {
      setResolvingSSM(false);
    }
  };

  // Re-resolve when the operator changes the System after an initial preview.
  useEffect(() => {
    if (!preview || !selectedSystem) return;
    let cancelled = false;
    (async () => {
      const enriched = await resolveSystemBuyerCodes(preview, selectedSystem);
      if (!cancelled) setPreview(enriched);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSystem]);

  const updateRow = (rowNum, patch) => {
    setPreview(prev => {
      if (!prev) return prev;
      const rows = prev.rows.map(r => (r.row_number === rowNum ? { ...r, ...patch } : r));
      // Recompute status locally so Post button reacts to corrections in real time
      const recomputed = rows.map(r => {
        const errors = [];
        if (!r.supplier_item_code) errors.push('Supplier Item Code required');
        if (r.quantity == null || r.quantity <= 0) errors.push('Quantity must be > 0');
        if (r.purchase_rate == null || r.purchase_rate < 0) errors.push('Purchase Rate must be a number');
        if (r.po_pending != null && r.quantity != null && r.quantity > r.po_pending) errors.push(`Exceeds pending (${r.po_pending})`);
        let status = r.product_id ? 'mapped' : 'unmapped';
        if (errors.length) status = 'error';
        return { ...r, errors, status };
      });
      const mapped = recomputed.filter(r => r.status === 'mapped').length;
      const unmapped = recomputed.filter(r => r.status === 'unmapped').length;
      const invalid = recomputed.filter(r => r.status === 'error').length;
      return { ...prev, rows: recomputed, summary: { total: recomputed.length, mapped, unmapped, invalid } };
    });
  };

  const openMapPicker = (row) => {
    setMappingRowNum(row.row_number);
    setMapPickerProductId(''); setMapPickerSearch('');
  };

  const saveMapping = async () => {
    if (!preview || mappingRowNum == null) return;
    const row = preview.rows.find(r => r.row_number === mappingRowNum);
    if (!row) return;
    if (!row.supplier_item_code) { toast.error('Fix the Supplier Item Code before mapping'); return; }
    if (!mapPickerProductId) { toast.error('Pick an existing product'); return; }
    setSavingMap(true);
    try {
      await axios.post(`${API_URL}/supplier-item-mappings`, {
        vendor_id: vendorId,
        product_id: mapPickerProductId,
        supplier_item_code: row.supplier_item_code,
        supplier_description: row.supplier_description || null,
        active: true,
      });
      toast.success('Mapping saved — refreshing preview to attach PO quantities');
      setMappingRowNum(null);
      await runPreview();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save mapping');
    } finally { setSavingMap(false); }
  };

  const allValid = preview
    && preview.summary.unmapped === 0
    && preview.summary.invalid === 0
    && (preview.summary.system_unmapped ?? 0) === 0
    && preview.summary.total > 0;

  // Create a System-Supplier mapping inline for the currently open row and
  // re-resolve the entire preview so the "Post" button unblocks. Supports
  // linking to an EXISTING Product (preferred) or CREATING a new Product
  // when genuinely needed. Never creates a duplicate Product — the picker
  // shows the whole Product Master and a client-side duplicate hint is shown
  // before creating a new one.
  const saveSsmMapping = async () => {
    if (!preview || ssmRowNum == null) return;
    const row = preview.rows.find(r => r.row_number === ssmRowNum);
    if (!row) return;
    if (!row.supplier_item_code) { toast.error('Supplier SKU is missing — correct it first'); return; }
    if (!ssmBuyerCode.trim()) { toast.error('Buyer Code is required'); return; }
    setSavingSsm(true);
    try {
      let productIdToUse = ssmProductId || null;
      // Create a new Product first when the user chose "Create new"
      if (ssmCreatingNew) {
        const np = ssmNewProduct;
        if (!np.name.trim()) { toast.error('New Product name is required'); setSavingSsm(false); return; }
        const payload = {
          name: np.name.trim(),
          brand: (np.brand || '').trim() || 'Generic',
          size: (np.size || '').trim() || null,
          hsn_code: (np.hsn_code || '').trim() || null,
          gst_percentage: Number(np.gst_percentage) || 0,
          purchase_price: Number(np.purchase_price) || 0,
        };
        const created = await axios.post(`${API_URL}/products`, payload);
        productIdToUse = created.data?.id;
        // Keep the local product list fresh so subsequent Map Now flows see it
        setAvailableProducts(prev => [created.data, ...prev]);
        toast.success(`Product "${payload.name}" added to Product Master`);
      }
      await axios.post(`${API_URL}/system-supplier-mappings`, {
        system: selectedSystem,
        supplier_sku: row.supplier_item_code,
        product_id: productIdToUse,
        buyer_item_code: ssmBuyerCode.trim(),
        buyer_item_description: ssmBuyerDesc.trim() || null,
        active: true,
      });
      toast.success('Mapping saved — re-resolving preview');
      setSsmRowNum(null); setSsmBuyerCode(''); setSsmBuyerDesc('');
      setSsmProductId(''); setSsmProductQuery(''); setSsmCreatingNew(false);
      setSsmNewProduct({ name: '', brand: '', size: '', hsn_code: '', gst_percentage: 5, purchase_price: 0 });
      const enriched = await resolveSystemBuyerCodes(preview, selectedSystem);
      setPreview(enriched);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save mapping');
    } finally { setSavingSsm(false); }
  };

  // Client-side duplicate hint for the "Create new Product" form — matches
  // on (brand + size + name substring), case-insensitive. Purely advisory;
  // the actual create still goes through the existing /products endpoint.
  const ssmProductDupHint = React.useMemo(() => {
    if (!ssmCreatingNew) return null;
    const np = ssmNewProduct;
    if (!np.name.trim()) return null;
    const n = np.name.trim().toLowerCase();
    const b = (np.brand || '').trim().toLowerCase();
    const s = (np.size || '').trim().toLowerCase();
    const hits = availableProducts.filter(p => {
      const pn = (p.name || '').toLowerCase();
      const pb = (p.brand || '').toLowerCase();
      const ps = (p.size || '').toLowerCase();
      const nameMatch = pn.includes(n) || n.includes(pn);
      const brandMatch = !b || pb === b;
      const sizeMatch = !s || ps === s;
      return nameMatch && brandMatch && sizeMatch;
    });
    return hits.slice(0, 3);
  }, [ssmCreatingNew, ssmNewProduct, availableProducts]);

  const postReceipt = async () => {
    if (!preview || !allValid) return;
    if (!poId) {
      toast.info('Select a Purchase Order to post this receipt against.');
      return;
    }
    if (!invoiceNumber.trim() || !invoiceDate) {
      toast.error('Invoice number and date are required to post');
      return;
    }
    setPosting(true);
    try {
      const payload = {
        po_id: poId,
        vendor_invoice_number: invoiceNumber,
        receipt_date: invoiceDate,
        items: preview.rows.filter(r => r.status === 'mapped').map(r => ({
          po_item_index: r.po_item_index,
          item_name: r.product_name || r.supplier_description || r.supplier_item_code,
          size: r.size || null,
          gh: null,
          batch_number: r.batch || `BATCH-${Date.now()}`,
          quantity: Math.round(r.quantity),
          unit_price: r.purchase_rate,
          gst_percentage: r.gst_percentage ?? 5.0,
        })),
        notes: `Supplier invoice import: ${invoiceNumber}`,
      };
      const res = await axios.post(`${API_URL}/purchase-orders/${poId}/receive`, payload);
      toast.success(res.data.message || 'Receipt posted');
      if (onPosted) onPosted();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to post receipt');
    } finally { setPosting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-6xl max-h-[92vh] overflow-y-auto" data-testid="supplier-invoice-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-teal-600" /> Import Supplier Invoice
          </DialogTitle>
          <DialogDescription>
            Upload the supplier invoice PDF (preferred) or an Excel/CSV file. Extracted rows can be edited before posting.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 rounded-md border p-3">
            <div className="space-y-1.5">
              <Label>Vendor *</Label>
              <Select value={vendorId} onValueChange={(v) => { setVendorId(v); setPoId(''); setPreview(null); }}>
                <SelectTrigger data-testid="si-vendor-select"><SelectValue placeholder="Select vendor" /></SelectTrigger>
                <SelectContent>
                  {(vendors || []).map(v => (
                    <SelectItem key={v.id} value={v.id} data-testid={`si-vendor-option-${v.id}`}>{v.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="flex items-center gap-1">
                <Layers className="w-3.5 h-3.5 text-teal-700" /> System *
              </Label>
              <Select value={selectedSystem} onValueChange={setSelectedSystem}>
                <SelectTrigger data-testid="si-system-select">
                  <SelectValue placeholder={systemsList.length === 0 ? 'No systems — import mappings first' : 'Select system'} />
                </SelectTrigger>
                <SelectContent>
                  {systemsList.map(s => (
                    <SelectItem key={s} value={s} data-testid={`si-system-option-${s}`}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="si-invoice-number">
                Invoice Number {isPdf ? <span className="text-xs text-slate-400">(auto-filled)</span> : ' *'}
              </Label>
              <Input id="si-invoice-number" value={invoiceNumber} onChange={(e) => setInvoiceNumber(e.target.value)} placeholder={isPdf ? 'Extracted from PDF' : 'e.g., INV-2025-001'} data-testid="si-invoice-number-input" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="si-invoice-date">
                Invoice Date {isPdf ? <span className="text-xs text-slate-400">(auto-filled)</span> : ' *'}
              </Label>
              <Input id="si-invoice-date" type="date" value={invoiceDate} onChange={(e) => setInvoiceDate(e.target.value)} data-testid="si-invoice-date-input" />
            </div>
          </div>

          <div className="rounded-md border p-3 bg-slate-50/60">
            <Label className="text-xs text-slate-500">Purchase Order <span className="text-xs text-slate-400">(optional — required only for posting receipt)</span></Label>
            <Select value={poId || '__none__'} onValueChange={(v) => { setPoId(v === '__none__' ? '' : v); setPreview(null); }} disabled={!vendorId}>
              <SelectTrigger data-testid="si-po-select" className="mt-1.5">
                <SelectValue placeholder={vendorId ? 'None — free-form receipt' : 'Select vendor first'} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">None — free-form receipt</SelectItem>
                {poList.map(po => (
                  <SelectItem key={po.id} value={po.id} data-testid={`si-po-option-${po.id}`}>
                    {po.po_number} · {PO_LABEL[po.status] || po.status}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {poDetail && (
            <div className="rounded-md border p-3 bg-slate-50" data-testid="si-po-summary">
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm">
                  <span className="font-semibold">{poDetail.po_number}</span>
                  <span className="text-slate-600 ml-2">· {PO_LABEL[poDetail.status] || poDetail.status}</span>
                </div>
                <div className="text-xs text-slate-600">
                  Ordered <span className="font-medium">{(poDetail.items || []).reduce((s, i) => s + (i.quantity || 0), 0)}</span> ·
                  Received <span className="font-medium">{(poDetail.items || []).reduce((s, i) => s + (i.received_quantity || 0), 0)}</span> ·
                  Pending <span className="font-medium">{(poDetail.items || []).reduce((s, i) => s + (i.pending_quantity ?? (i.quantity - (i.received_quantity || 0))), 0)}</span>
                </div>
              </div>
            </div>
          )}

          <div className="flex items-center justify-between gap-3 rounded-md border p-3">
            <div className="flex items-center gap-3 flex-wrap">
              <label className="cursor-pointer">
                <input type="file" accept=".pdf,.csv,.xlsx,.xls" onChange={handleFileChange} className="hidden" data-testid="si-file-input" />
                <span className="inline-flex items-center px-3 py-2 rounded-md border bg-white text-sm hover:bg-slate-50">
                  <Upload className="w-4 h-4 mr-2" /> Choose file
                </span>
              </label>
              <span className="text-xs text-slate-600" data-testid="si-file-name">
                {file ? (
                  <>
                    <Badge className={cn('mr-2', isPdf ? 'bg-teal-100 text-teal-700 hover:bg-teal-100' : 'bg-slate-100 text-slate-700 hover:bg-slate-100')}>
                      {isPdf ? 'PDF' : 'Excel/CSV'}
                    </Badge>
                    {file.name}
                  </>
                ) : 'No file selected — PDF preferred, Excel/CSV also supported'}
              </span>
              <Button type="button" variant="outline" size="sm" onClick={downloadTemplate} data-testid="si-template-btn">
                <FileSpreadsheet className="w-4 h-4 mr-2" /> Excel Template
              </Button>
            </div>
            <Button type="button" onClick={runPreview} disabled={!file || previewing || resolvingSSM || !vendorId || !selectedSystem || (!isPdf && (!invoiceNumber || !invoiceDate))} className="bg-teal-600 hover:bg-teal-700" data-testid="si-preview-btn">
              {previewing ? (isPdf ? 'Extracting…' : 'Parsing…') : resolvingSSM ? 'Resolving…' : (isPdf ? <><Sparkles className="w-4 h-4 mr-2" />Extract & Preview</> : 'Preview')}
            </Button>
          </div>

          {preview && (
            <div className="space-y-2" data-testid="si-preview-block">
              <div className="flex items-center gap-2 text-sm flex-wrap">
                <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100"><CheckCircle2 className="w-3.5 h-3.5 mr-1" /> {preview.summary.mapped} mapped</Badge>
                <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100"><Link2 className="w-3.5 h-3.5 mr-1" /> {preview.summary.unmapped} unmapped (product)</Badge>
                {(preview.summary.system_unmapped ?? 0) > 0 && (
                  <Badge className="bg-rose-100 text-rose-700 hover:bg-rose-100"><AlertTriangle className="w-3.5 h-3.5 mr-1" /> {preview.summary.system_unmapped} without Buyer Code ({selectedSystem})</Badge>
                )}
                {preview.summary.invalid > 0 && (
                  <Badge className="bg-rose-100 text-rose-700 hover:bg-rose-100"><AlertTriangle className="w-3.5 h-3.5 mr-1" /> {preview.summary.invalid} need review</Badge>
                )}
                {preview.extraction_method && (
                  <Badge variant="secondary" className="bg-slate-100 text-slate-700">Source: {preview.extraction_method.toUpperCase()}</Badge>
                )}
                {selectedSystem && (
                  <Badge variant="secondary" className="bg-teal-50 text-teal-700 border border-teal-100">System: {selectedSystem}</Badge>
                )}
                <span className="text-xs text-slate-500">of {preview.summary.total} line(s) · Edit any cell inline before posting.</span>
              </div>
              <ScrollArea className="max-h-[440px] rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[52px]">Row</TableHead>
                      <TableHead className="w-[140px]">Supplier Code</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead className="w-[80px]">Qty</TableHead>
                      <TableHead className="w-[100px]">Rate</TableHead>
                      <TableHead className="w-[110px]">Batch</TableHead>
                      <TableHead className="w-[130px]">Expiry</TableHead>
                      <TableHead className="w-[160px]">Buyer Code (System)</TableHead>
                      <TableHead>Product</TableHead>
                      {poId && <TableHead className="text-right w-[60px]">Ord</TableHead>}
                      {poId && <TableHead className="text-right w-[60px]">Recd</TableHead>}
                      {poId && <TableHead className="text-right w-[60px]">Pend</TableHead>}
                      <TableHead className="w-[120px]">Status</TableHead>
                      <TableHead className="w-[90px]">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {preview.rows.map(r => (
                      <TableRow key={r.row_number} className={cn(r.status === 'unmapped' && 'bg-amber-50/60', r.status === 'error' && 'bg-rose-50/60')} data-testid={`si-row-${r.row_number}`}>
                        <TableCell className="tabular-nums">{r.row_number}</TableCell>
                        <TableCell>
                          <EditCell value={r.supplier_item_code} onChange={(v) => updateRow(r.row_number, { supplier_item_code: v })} testid={`si-code-${r.row_number}`} placeholder="SKU" />
                        </TableCell>
                        <TableCell className="text-slate-600 text-xs">{r.supplier_description || '—'}</TableCell>
                        <TableCell>
                          <EditCell value={r.quantity} onChange={(v) => updateRow(r.row_number, { quantity: v })} type="number" testid={`si-qty-${r.row_number}`} className="text-right" />
                        </TableCell>
                        <TableCell>
                          <EditCell value={r.purchase_rate} onChange={(v) => updateRow(r.row_number, { purchase_rate: v })} type="number" testid={`si-rate-${r.row_number}`} className="text-right" />
                        </TableCell>
                        <TableCell>
                          <EditCell
                            value={r.batch}
                            onChange={(v) => updateRow(r.row_number, { batch: v })}
                            testid={`si-batch-${r.row_number}`}
                            className={cn((r.low_confidence || []).includes('batch') && !r.batch && 'border-amber-300 bg-amber-50')}
                            placeholder={(r.low_confidence || []).includes('batch') ? 'Not extracted' : ''}
                          />
                        </TableCell>
                        <TableCell>
                          <EditCell
                            value={r.expiry}
                            onChange={(v) => updateRow(r.row_number, { expiry: v })}
                            testid={`si-expiry-${r.row_number}`}
                            className={cn((r.low_confidence || []).includes('expiry') && !r.expiry && 'border-amber-300 bg-amber-50')}
                            placeholder={(r.low_confidence || []).includes('expiry') ? 'Not extracted' : ''}
                          />
                        </TableCell>
                        <TableCell>
                          {r.system_mapping_status === 'mapped' ? (
                            <div className="text-sm" data-testid={`si-buyer-code-${r.row_number}`}>
                              <div className="font-medium text-teal-800">{r.system_buyer_code}</div>
                              <div className="text-[11px] text-slate-500">{r.system_buyer_description || '—'}</div>
                            </div>
                          ) : (
                            <div className="space-y-1" data-testid={`si-buyer-unmapped-${r.row_number}`}>
                              <Badge className="bg-rose-100 text-rose-700 hover:bg-rose-100">MAPPING NOT FOUND</Badge>
                              <div>
                                <Button type="button" size="sm" variant="outline"
                                  disabled={!r.supplier_item_code || !selectedSystem}
                                  onClick={() => { setSsmRowNum(r.row_number); setSsmBuyerCode(''); setSsmBuyerDesc(''); }}
                                  data-testid={`si-map-now-${r.row_number}`}
                                  className="h-6 px-2 text-[11px]">Map Now</Button>
                              </div>
                            </div>
                          )}
                        </TableCell>
                        <TableCell>
                          {r.product_name ? (
                            <div className="text-sm">
                              <div className="font-medium">{r.product_name}</div>
                              <div className="text-[11px] text-slate-500">{r.brand}{r.brand && r.size ? ' · ' : ''}{r.size}{r.buyer_item_code ? ` · ${r.buyer_item_code}` : ''}</div>
                            </div>
                          ) : <span className="text-slate-400">—</span>}
                        </TableCell>
                        {poId && <TableCell className="tabular-nums text-right">{r.po_ordered ?? '—'}</TableCell>}
                        {poId && <TableCell className="tabular-nums text-right">{r.po_received ?? '—'}</TableCell>}
                        {poId && <TableCell className="tabular-nums text-right">{r.po_pending ?? '—'}</TableCell>}
                        <TableCell>
                          {r.status === 'mapped' && <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Mapped</Badge>}
                          {r.status === 'unmapped' && <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">UNMAPPED</Badge>}
                          {r.status === 'error' && (
                            <div className="space-y-1">
                              <Badge className="bg-rose-100 text-rose-700 hover:bg-rose-100">Review</Badge>
                              <div className="text-[10px] text-rose-700">{(r.errors || []).join(', ')}</div>
                            </div>
                          )}
                        </TableCell>
                        <TableCell>
                          {r.product_id == null && (
                            <Button type="button" variant="outline" size="sm" onClick={() => openMapPicker(r)} data-testid={`si-map-btn-${r.row_number}`}>Map Item</Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </ScrollArea>
            </div>
          )}

          {mappingRowNum != null && preview && (
            <div className="rounded-md border bg-slate-50 p-3" data-testid="si-map-panel">
              {(() => {
                const row = preview.rows.find(r => r.row_number === mappingRowNum);
                if (!row) return null;
                return (
                  <>
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <h4 className="text-sm font-semibold">Map Item — Row {row.row_number}</h4>
                        <p className="text-xs text-slate-600">Supplier code <span className="font-medium">{row.supplier_item_code || '(empty — correct it first)'}</span> → pick an existing product</p>
                      </div>
                      <Button type="button" variant="ghost" size="sm" onClick={() => setMappingRowNum(null)}>Close</Button>
                    </div>
                    <div className="grid gap-2">
                      <Input placeholder="Search product…" value={mapPickerSearch} onChange={(e) => setMapPickerSearch(e.target.value)} data-testid="si-map-search" />
                      <div className="max-h-[180px] overflow-y-auto rounded-md border bg-white">
                        {filteredProducts.length === 0 ? (
                          <div className="p-3 text-xs text-slate-500">No products found.</div>
                        ) : (
                          filteredProducts.slice(0, 200).map(p => (
                            <button type="button" key={p.id} onClick={() => setMapPickerProductId(p.id)}
                              className={cn('w-full text-left px-3 py-2 text-sm hover:bg-slate-100', mapPickerProductId === p.id && 'bg-teal-50 text-teal-800')}
                              data-testid={`si-map-product-${p.id}`}>
                              <div className="font-medium">{p.name}</div>
                              <div className="text-[11px] text-slate-500">{p.brand}{p.brand && p.size ? ' · ' : ''}{p.size}{p.buyer_item_code ? ` · ${p.buyer_item_code}` : ''}</div>
                            </button>
                          ))
                        )}
                      </div>
                      <div className="flex justify-end gap-2 pt-1">
                        <Button type="button" variant="outline" size="sm" onClick={() => setMappingRowNum(null)}>Cancel</Button>
                        <Button type="button" size="sm" onClick={saveMapping} disabled={!mapPickerProductId || savingMap || !row.supplier_item_code} className="bg-teal-600 hover:bg-teal-700" data-testid="si-map-save-btn">
                          {savingMap ? 'Saving…' : 'Save Mapping'}
                        </Button>
                      </div>
                    </div>
                  </>
                );
              })()}
            </div>
          )}
          {ssmRowNum != null && preview && (
            <div className="rounded-md border border-teal-200 bg-teal-50/50 p-3" data-testid="si-ssm-panel">
              {(() => {
                const row = preview.rows.find(r => r.row_number === ssmRowNum);
                if (!row) return null;
                return (
                  <>
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <h4 className="text-sm font-semibold flex items-center gap-1.5">
                          <Layers className="w-4 h-4 text-teal-700" /> Map Buyer Code — Row {row.row_number}
                        </h4>
                        <p className="text-xs text-slate-600">
                          Save a permanent mapping for <span className="font-medium">{selectedSystem}</span> + SKU <span className="font-medium">{row.supplier_item_code || '(empty)'}</span> → Buyer Code
                        </p>
                      </div>
                      <Button type="button" variant="ghost" size="sm" onClick={() => { setSsmRowNum(null); setSsmBuyerCode(''); setSsmBuyerDesc(''); }}>Close</Button>
                    </div>
                    <div className="grid gap-2 md:grid-cols-2">
                      <div className="space-y-1">
                        <Label className="text-xs">Buyer Code *</Label>
                        <Input value={ssmBuyerCode} onChange={(e) => setSsmBuyerCode(e.target.value)} placeholder="e.g., DEN-IMP-024" data-testid="si-ssm-buyer-code" />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Buyer Description</Label>
                        <Input value={ssmBuyerDesc} onChange={(e) => setSsmBuyerDesc(e.target.value)} placeholder="e.g., Implant - Narrow - Medium" data-testid="si-ssm-buyer-desc" />
                      </div>
                    </div>

                    {/* Product linkage — pick from Product Master, or create a new one */}
                    <div className="mt-3 rounded-md border border-slate-200 bg-white/70 p-3">
                      <div className="flex items-center justify-between mb-2">
                        <Label className="text-xs font-semibold text-slate-700">Internal Product</Label>
                        <div className="flex gap-1 text-xs">
                          <button type="button" onClick={() => { setSsmCreatingNew(false); }}
                            className={cn("px-2 py-0.5 rounded", !ssmCreatingNew ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100")}
                            data-testid="si-ssm-mode-existing">Existing</button>
                          <button type="button" onClick={() => { setSsmCreatingNew(true); setSsmProductId(''); }}
                            className={cn("px-2 py-0.5 rounded", ssmCreatingNew ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100")}
                            data-testid="si-ssm-mode-create">Create new</button>
                        </div>
                      </div>

                      {!ssmCreatingNew ? (
                        <div className="space-y-1">
                          {ssmProductId ? (
                            (() => {
                              const p = availableProducts.find(x => x.id === ssmProductId);
                              return (
                                <div className="flex items-center justify-between border border-slate-200 rounded p-2 bg-slate-50/60">
                                  <div className="text-sm">
                                    <div className="font-medium text-slate-800">{p?.name || 'Selected product'}</div>
                                    <div className="text-[11px] text-slate-500">{p?.brand} · {p?.size || '—'}</div>
                                  </div>
                                  <Button type="button" variant="ghost" size="sm" onClick={() => setSsmProductId('')} data-testid="si-ssm-product-clear">Clear</Button>
                                </div>
                              );
                            })()
                          ) : (
                            <>
                              <Input value={ssmProductQuery} onChange={(e) => setSsmProductQuery(e.target.value)} placeholder="Search Product Master by name / brand / size…" data-testid="si-ssm-product-search" className="h-8 text-sm" />
                              {ssmProductQuery && (
                                <div className="max-h-40 overflow-auto border border-slate-200 rounded">
                                  {availableProducts
                                    .filter(p => {
                                      const q = ssmProductQuery.toLowerCase();
                                      return (p.name || '').toLowerCase().includes(q) || (p.brand || '').toLowerCase().includes(q) || (p.size || '').toLowerCase().includes(q);
                                    })
                                    .slice(0, 20)
                                    .map(p => (
                                      <button key={p.id} type="button" onClick={() => { setSsmProductId(p.id); setSsmProductQuery(''); }}
                                        className="w-full text-left px-2.5 py-1.5 hover:bg-slate-50 border-b border-slate-100 last:border-b-0 text-sm"
                                        data-testid={`si-ssm-product-opt-${p.id}`}>
                                        <div className="font-medium text-slate-800">{p.name}</div>
                                        <div className="text-[11px] text-slate-500">{p.brand} · {p.size || '—'}</div>
                                      </button>
                                    ))}
                                </div>
                              )}
                              <p className="text-[11px] text-slate-500">Optional — leave empty if you only need Buyer Code resolution. Recommended for inventory continuity.</p>
                            </>
                          )}
                        </div>
                      ) : (
                        <div className="space-y-2">
                          <div className="grid gap-2 md:grid-cols-3">
                            <div className="space-y-1">
                              <Label className="text-[11px]">Name *</Label>
                              <Input value={ssmNewProduct.name} onChange={(e) => setSsmNewProduct({ ...ssmNewProduct, name: e.target.value })} placeholder="Dental Implant" data-testid="si-ssm-new-name" className="h-8" />
                            </div>
                            <div className="space-y-1">
                              <Label className="text-[11px]">Brand</Label>
                              <Input value={ssmNewProduct.brand} onChange={(e) => setSsmNewProduct({ ...ssmNewProduct, brand: e.target.value })} placeholder={selectedSystem || 'Generic'} data-testid="si-ssm-new-brand" className="h-8" />
                            </div>
                            <div className="space-y-1">
                              <Label className="text-[11px]">Size</Label>
                              <Input value={ssmNewProduct.size} onChange={(e) => setSsmNewProduct({ ...ssmNewProduct, size: e.target.value })} placeholder="3.5mm x 11.5mm" data-testid="si-ssm-new-size" className="h-8" />
                            </div>
                          </div>
                          <div className="grid gap-2 md:grid-cols-3">
                            <div className="space-y-1">
                              <Label className="text-[11px]">HSN Code</Label>
                              <Input value={ssmNewProduct.hsn_code} onChange={(e) => setSsmNewProduct({ ...ssmNewProduct, hsn_code: e.target.value })} placeholder="90212900" data-testid="si-ssm-new-hsn" className="h-8" />
                            </div>
                            <div className="space-y-1">
                              <Label className="text-[11px]">GST %</Label>
                              <Input type="number" value={ssmNewProduct.gst_percentage} onChange={(e) => setSsmNewProduct({ ...ssmNewProduct, gst_percentage: e.target.value })} data-testid="si-ssm-new-gst" className="h-8" />
                            </div>
                            <div className="space-y-1">
                              <Label className="text-[11px]">Purchase Price</Label>
                              <Input type="number" value={ssmNewProduct.purchase_price} onChange={(e) => setSsmNewProduct({ ...ssmNewProduct, purchase_price: e.target.value })} placeholder={String(row.purchase_rate || 0)} data-testid="si-ssm-new-price" className="h-8" />
                            </div>
                          </div>
                          {ssmProductDupHint && ssmProductDupHint.length > 0 && (
                            <div className="rounded border border-amber-200 bg-amber-50 p-2 text-xs">
                              <div className="flex items-center gap-1 text-amber-800 font-medium mb-1">
                                <AlertTriangle className="w-3.5 h-3.5" /> Possible existing Product{ssmProductDupHint.length > 1 ? 's' : ''}
                              </div>
                              {ssmProductDupHint.map(p => (
                                <div key={p.id} className="flex items-center justify-between py-0.5">
                                  <span className="text-slate-700">{p.name} <span className="text-slate-500">· {p.brand} · {p.size || '—'}</span></span>
                                  <button type="button" onClick={() => { setSsmCreatingNew(false); setSsmProductId(p.id); }} className="text-teal-700 hover:text-teal-900 underline">Use this instead</button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="flex justify-end gap-2 pt-2">
                      <Button type="button" variant="outline" size="sm" onClick={() => { setSsmRowNum(null); setSsmBuyerCode(''); setSsmBuyerDesc(''); }}>Cancel</Button>
                      <Button type="button" size="sm" onClick={saveSsmMapping} disabled={!ssmBuyerCode.trim() || savingSsm || !row.supplier_item_code} className="bg-teal-600 hover:bg-teal-700" data-testid="si-ssm-save">
                        {savingSsm ? 'Saving…' : 'Save & Re-resolve'}
                      </Button>
                    </div>
                  </>
                );
              })()}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
          <Button type="button" onClick={postReceipt} disabled={!allValid || !poId || posting} className="bg-teal-600 hover:bg-teal-700" data-testid="si-continue-btn">
            {posting ? 'Posting…'
              : !poId ? 'Select a PO to post'
              : (preview && (preview.summary.system_unmapped ?? 0) > 0) ? `Map ${preview.summary.system_unmapped} Buyer Code(s) first`
              : 'Post Receipt to PO'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SupplierInvoiceImport;
